import * as XLSX from 'xlsx';
import { ImportRow, WorkbookSource } from './workbookImport';

export type VerificationStatus = 'domain_valid' | 'deliverable' | 'invalid' | 'risky' | 'unknown';
export interface RecipientVerification {
    email: string;
    status: VerificationStatus;
    reason: string;
    code: string;
    method: 'syntax' | 'dns' | 'emailable';
    checkedAt: string;
    suggestion?: string;
}
export interface VerificationConfig {
    local: boolean;
    maxRows: number;
    batchSize: number;
    mailboxBatchSize: number;
    mailboxConfigured: boolean;
    mailboxMessage: string;
}
export const VERIFICATION_LABELS: Record<VerificationStatus | 'unchecked', string> = {
    domain_valid: '域名通过 · 邮箱未确认', deliverable: '验证可投递',
    invalid: '无效', risky: '需要核对', unknown: '无法确认', unchecked: '未检查',
};
export const emailKey = (email: string) => email.trim().toLowerCase();
export const LOCAL_VERIFICATION_AVAILABLE = process.env.NODE_ENV === 'development'
    && ['127.0.0.1', 'localhost'].includes(window.location.hostname);

export async function verificationRequest<T>(action: 'config' | 'check', body: object = {}, signal?: AbortSignal): Promise<T> {
    if (!LOCAL_VERIFICATION_AVAILABLE) throw new Error('请在本地项目中打开邮箱检查。');
    const response = await fetch(`/__lawhelper/recipient-verification/${action}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Lawhelper-Local-Device': '1' },
        body: JSON.stringify(body), signal,
    });
    if (!(response.headers.get('content-type') || '').includes('application/json')) throw new Error('本地检查接口尚未就绪，请重启本地网页服务。');
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || '邮箱检查失败，请稍后重试。');
    return data as T;
}

export function retainedEmails(results: Map<string, RecipientVerification>, keepDomainOnly: boolean, keepUncertain: boolean): Set<string> {
    return new Set(Array.from(results.values()).filter(result => result.email && (
        result.status === 'deliverable' || (keepDomainOnly && result.status === 'domain_valid')
        || (keepUncertain && ['risky', 'unknown'].includes(result.status))
    )).map(result => result.email));
}

export function verificationExportRows(rows: ImportRow[], results: Map<string, RecipientVerification>, kept: Set<string>) {
    return rows.map(row => {
        const result = results.get(emailKey(row.email));
        return {
            '邮箱': row.email, '姓名': row.name, '职位': row.title, '公司': row.companyName,
            '国家': row.country, '国家代码': row.countryCode, '语言': row.language, '来源网址': row.sourceUrl,
            '来源工作表': row.sourceSheet, '来源行号': row.sourceRow,
            ...Object.fromEntries(Object.entries(row.customFields).map(([key, value]) => [`变量：${key}`, value])),
            '有效性结果': VERIFICATION_LABELS[result?.status || 'unchecked'],
            '检查说明': result?.reason || '尚未检查，此行未保留', '建议邮箱': result?.suggestion || '',
            '检查方式': result?.method === 'emailable' ? 'Emailable' : result?.method === 'dns' ? '本地 DNS' : result ? '本地格式' : '',
            '检查时间': result?.checkedAt || '', '本次保留': kept.has(emailKey(row.email)) ? '是' : '否',
        };
    });
}

// The report keeps every original worksheet/cell, including columns that are
// not template variables. Append a separate result sheet; never rewrite data.
export function buildVerificationWorkbook(rows: ImportRow[], results: Map<string, RecipientVerification>, kept: Set<string>, source?: WorkbookSource, subset: 'all' | 'kept' | 'excluded' = 'all') {
    const book: XLSX.WorkBook = source && subset === 'all'
        ? { ...source.workbook, SheetNames: [...source.workbook.SheetNames], Sheets: { ...source.workbook.Sheets } }
        : XLSX.utils.book_new();
    const selected = subset === 'all' ? rows : rows.filter(row => kept.has(emailKey(row.email)) === (subset === 'kept'));
    if (source && subset !== 'all') {
        // Keep original headers/custom variable names when a filtered file is
        // re-imported. Freeze formulas to their displayed/cached values because
        // row numbers change when unwanted contacts are removed.
        for (const parsed of source.sheets.filter(sheet => sheet.selected && !sheet.issue)) {
            const sheetRows = selected.filter(row => row.sourceSheet === parsed.name);
            if (!sheetRows.length) continue;
            const original = source.workbook.Sheets[parsed.name];
            const width = Math.max(...parsed.columns.map(column => column.index)) + 1;
            const values = (rowIndex: number) => Array.from({ length: width }, (_, column) => {
                const cell = original[XLSX.utils.encode_cell({ r: rowIndex, c: column })];
                return cell?.w ?? cell?.v ?? '';
            });
            const header = parsed.headerRowIndex >= 0 ? values(parsed.headerRowIndex)
                : Array.from({ length: width }, (_, column) => column === parsed.emailColumn ? '邮箱' : `列 ${XLSX.utils.encode_col(column)}`);
            XLSX.utils.book_append_sheet(book, XLSX.utils.aoa_to_sheet([header, ...sheetRows.map(row => values(row.sourceRow - 1))]), parsed.name);
        }
        if (book.SheetNames.length) return book;
    }
    const data = verificationExportRows(selected, results, kept);
    const sheet = XLSX.utils.json_to_sheet(data);
    sheet['!cols'] = Object.keys(data[0] || {}).map(key => ({ wch: key === '检查说明' ? 60 : 25 }));
    let name = subset === 'all' ? '邮箱有效性结果' : subset === 'kept' ? '保留名单' : '未保留名单';
    const base = name;
    for (let index = 2; book.SheetNames.includes(name); index++) name = `${base} ${index}`;
    XLSX.utils.book_append_sheet(book, sheet, name);
    return book;
}
